import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ActionDropdown } from "components/common";
import { FaUserTie, FaSitemap } from 'react-icons/fa';
import ChangeManagerForm from './employee/ChangeManagerForm';
import { FaPlus } from "react-icons/fa6";
import { MdFormatListBulleted, MdSync, MdCloudSync, MdOutlineMultipleStop, MdSwapHoriz } from 'react-icons/md';
import CrmListPage from "components/app/crm/shared/CrmListPage";
import { columnDefs as baseColumns } from "./employee/component/Columns";
import SyncNewEmployeeConfirm from "./employee/component/SyncNewEmployeeConfirm";
import SyncTitleEmployeeChangedConfirm from "./employee/component/SyncTitleEmployeeChangedConfirm";
import SyncTerminalEmployeeConfirm from "./employee/component/SyncTerminalEmployeeConfirm";
import { getAllEmployees, exportEmployees, getPositionGroups } from "redux/organization/employeeSlice";
import { getAllDepartments } from "redux/organization/departmentSlice";
import { getAllSites } from "redux/organization/siteSlice";

const columns = baseColumns.map((c) => ({ ...c, sortable: false }));

const TABS = [{ key: "all", label: "All", Icon: MdFormatListBulleted, thunk: getAllEmployees, dataKey: "allData" }];

const EmployeeListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showSyncNew, setShowSyncNew] = useState(false);
  const [managerSelection, setManagerSelection] = useState(null);
  const [showSyncTitle, setShowSyncTitle] = useState(false);
  const [showSyncTerminal, setShowSyncTerminal] = useState(false);

  const { data: departmentData = [] } = useSelector((s) => s.organization_department.allData);
  const { data: siteData = [] } = useSelector((s) => s.organization_site.allData);
  const { positionGroups = [] } = useSelector((s) => s.organization_employee);

  useEffect(() => {
    dispatch(getAllDepartments());
    dispatch(getAllSites());
    dispatch(getPositionGroups());
  }, [dispatch]);

  // Departments grouped by site so same-named departments from different sites
  // are distinguishable (group headers in the multi-select dropdown).
  const departmentGroups = useMemo(() => {
    const bySite = departmentData.reduce((acc, d) => {
      const site = d.site_name || "No site";
      (acc[site] ||= []).push({ value: d.id, label: d.name });
      return acc;
    }, {});

    return Object.entries(bySite)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([site, items]) => ({ key: site, label: site, items: items.sort((x, y) => x.label.localeCompare(y.label)) }));
  }, [departmentData]);

  // Multi-select quick filters (site + department) in the search bar.
  const multiSelects = useMemo(() => [
    {
      key: "site",
      paramKey: "site_id_in",
      placeholder: "All sites",
      options: siteData.map((s) => ({ value: s.id, label: s.name })),
      width: 220
    },
    {
      key: "department",
      paramKey: "department_id_in",
      placeholder: "All departments",
      groups: departmentGroups,
      width: 260
    }
  ], [siteData, departmentGroups]);

  // Single-select quick filters in the search bar.
  const selects = useMemo(() => [
    {
      key: "status",
      paramKey: "active_eq",
      options: [
        { value: "", label: "All status" },
        { value: "true", label: "Active" },
        { value: "false", label: "Deactivated" }
      ]
    }
  ], []);

  // Inline "Created" date-range filter surfaced in the search bar.
  const dateFilters = useMemo(() => [
    { key: "created_at", label: "Created", paramKeys: ["created_at_gteq", "created_at_lteq"] }
  ], []);

  const advancedFields = useMemo(() => [
    { key: "id",            label: "ID#",          type: "text",        paramKey: "id_cont",           tagName: "ID#" },
    { key: "name",          label: "Name",         type: "text",        paramKey: "name_cont",         tagName: "Name" },
    { key: "full_name",     label: "Full of name", type: "text",        paramKey: "full_name_cont",    tagName: "Full name" },
    { key: "email",         label: "Email",        type: "text",        paramKey: "email_cont",        tagName: "Email" },
    { key: "title",         label: "Title",        type: "text",        paramKey: "title_cont",        tagName: "Title" },
    { key: "phone_number",  label: "Phone number", type: "text",        paramKey: "phone_number_cont", tagName: "Phone" },
    { key: "hr_code",       label: "HR code",      type: "text",        paramKey: "hr_code_cont",      tagName: "HR" },
    { key: "position_group", label: "Position group", type: "multiselect", options: positionGroups.map((p) => ({ value: p.id, label: p.name })), paramKey: "position_group_id_in", tagName: "PG" },
    { key: "is_sales",      label: "Sales team only", type: "checkbox", paramKey: "is_sales_true", tagName: "Sales" }
  ], [positionGroups]);

  // Selection action: reassign several employees to one manager in a single step.
  // The modal itself is shared with the org chart, so the confirmation reads the
  // same wherever the change starts.
  const renderSelectionActions = (selectedRows, triggerReload) => (
    <>
      <button
        type="button"
        onClick={() => setManagerSelection(selectedRows)}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-slate-300 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 tw-text-sm tw-font-medium tw-cursor-pointer">
        <MdSwapHoriz size={15} /> Change manager ({selectedRows.length})
      </button>
      {managerSelection && (
        <ChangeManagerForm
          open
          setOpen={() => setManagerSelection(null)}
          records={managerSelection}
          onSuccess={triggerReload}
        />
      )}
    </>
  );

  const headerExtra = (
    <>
      <ActionDropdown
        align="end"
        menuWidth={230}
        unstyledTrigger
        triggerClassName="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-sky-300 tw-bg-sky-50 tw-text-sky-700 hover:tw-bg-sky-100 tw-text-sm tw-font-medium tw-cursor-pointer"
        trigger={<><MdSync size={14} /> Sync iHRM</>}
        items={[
          { label: 'Sync new employee', icon: <MdCloudSync size={14} />, onClick: () => setShowSyncNew(true) },
          { label: 'Sync title employee changed', icon: <MdSync size={14} />, onClick: () => setShowSyncTitle(true) },
          { label: 'Sync terminal employee', icon: <MdOutlineMultipleStop size={14} />, onClick: () => setShowSyncTerminal(true) },
        ]}
      />

      <button type="button" onClick={() => navigate("/organization/employees/org-chart")}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-slate-300 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 tw-text-sm tw-font-medium tw-cursor-pointer">
        <FaSitemap size={12} /> Org chart
      </button>

      <button type="button" onClick={() => navigate("/organization/employees/new")}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-primary-600 tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-text-sm tw-font-medium tw-cursor-pointer">
        <FaPlus size={11} /> New Employee
      </button>
    </>
  );

  return (
    <>
      <CrmListPage
        title="Employees"
        subtitle="Organization employee directory."
        Icon={FaUserTie}
        reducerSelector={(s) => s.organization_employee}
        tabs={TABS}
        columns={columns}
        searchParamKey="name_or_full_name_or_title_or_hr_code_or_phone_number_unaccent_cont"
        searchPlaceholder="Name, title, HR code, phone..."
        dateFilters={dateFilters}
        multiSelects={multiSelects}
        selects={selects}
        advancedFields={advancedFields}
        advancedTitle="Filters for employees"
        exportThunk={exportEmployees}
        headerExtra={headerExtra}
        enableSelection
        renderSelectionActions={renderSelectionActions}
      />

      {showSyncNew && (
        <SyncNewEmployeeConfirm showModal={showSyncNew} setshowModal={setShowSyncNew} />
      )}
      {showSyncTitle && (
        <SyncTitleEmployeeChangedConfirm showModal={showSyncTitle} setshowModal={setShowSyncTitle} />
      )}
      {showSyncTerminal && (
        <SyncTerminalEmployeeConfirm showModal={showSyncTerminal} setshowModal={setShowSyncTerminal} />
      )}
    </>
  );
};

export default EmployeeListPage;
